import React, { Component } from 'react';
import { Checkbox, FormControlLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { Annotation } from '@paragon/contract-library';

import AnnotationOptionsSection, { AnnotationsOptionsRow } from './AnnotationOptionsSection';
import FloatInput from './FloatInput';

interface Props {
  readonly itemBeingEdited?: paper.Item;
  readonly annotationBeingEdited: Annotation | undefined;
  readonly resizingDisabled: boolean;
  readonly individualScalingDisabled: boolean;
  readonly onAdjustFontSize: (scalingPercentage: number) => void;
}

type Unit = 'px' | 'in';

interface State {
  readonly units: Unit;
  readonly boxWidth: number;
  readonly boxHeight: number;
  readonly scaleTogether: boolean;
}

export default class SizeOptions extends Component<Props, State> {
  state: State = {
    units: 'px',

    boxWidth:
      this.props.annotationBeingEdited && this.props.itemBeingEdited
        ? (
            ('height' in this.props.itemBeingEdited && 'width' in this.props.itemBeingEdited
              ? this.props.itemBeingEdited
              : this.props.itemBeingEdited.internalBounds) as { width: number }
          ).width * this.props.itemBeingEdited.scaling.x
        : 0,
    boxHeight:
      this.props.annotationBeingEdited && this.props.itemBeingEdited
        ? (
            ('height' in this.props.itemBeingEdited && 'width' in this.props.itemBeingEdited
              ? this.props.itemBeingEdited
              : this.props.itemBeingEdited.internalBounds) as { height: number }
          ).height * this.props.itemBeingEdited.scaling.y
        : 0,

    scaleTogether: true, // TODO: = isTextAnnotation
  };

  onChangeWidth = (newWidth: number, oldWidth: number) => {
    this.setState({ boxWidth: newWidth });

    const scalingPercentage = newWidth / oldWidth;

    this.props.onAdjustFontSize(scalingPercentage);

    if (this.state.scaleTogether) {
      this.adjustHeight(scalingPercentage);
    }
  };

  onChangeHeight = (newHeight: number, oldHeight: number) => {
    this.setState({ boxHeight: newHeight });

    const scalingPercentage = newHeight / oldHeight;

    this.props.onAdjustFontSize(scalingPercentage);

    if (this.state.scaleTogether) {
      this.adjustWidth(scalingPercentage);
    }
  };

  onChangeUnits = (event: SelectChangeEvent<Unit>) => {
    // Conversion factor for pixels to inches or vice versa
    const conversionMultiplier = event.target.value === 'px' ? 180.0 : 1 / 180.0;

    this.setState((prevState) => ({
      units: event.target.value as Unit,

      boxWidth: prevState.boxWidth * conversionMultiplier,
      boxHeight: prevState.boxHeight * conversionMultiplier,
    }));
  };

  onChangeScaleTogether = () => {
    this.setState((prevState) => ({
      scaleTogether: !prevState.scaleTogether,
    }));
  };

  get updatedProperties() {
    let { boxWidth: width, boxHeight: height } = this.state;

    width /= this.props.itemBeingEdited?.scaling.x ?? 1;
    height /= this.props.itemBeingEdited?.scaling.y ?? 1;

    if (this.state.units === 'in') {
      width *= 180;
      height *= 180;
    }

    return {
      newBoundingBoxWidth: width,
      newBoundingBoxHeight: height,
    };
  }

  adjustWidth = (scalingPercentage: number) => {
    this.setState((prevState) => ({
      boxWidth: prevState.boxWidth * scalingPercentage,
    }));
  };

  adjustHeight = (scalingPercentage: number) => {
    this.setState((prevState) => ({
      boxHeight: prevState.boxHeight * scalingPercentage,
    }));
  };

  adjustDimensions = (scalingPercentage: number) => {
    this.setState((prevState) => ({
      boxWidth: prevState.boxWidth * scalingPercentage,
      boxHeight: prevState.boxHeight * scalingPercentage,
    }));
  };

  render() {
    return (
      <AnnotationOptionsSection title="size">
        <AnnotationsOptionsRow>
          <FloatInput
            label="Width"
            id="bounding-box-width-input"
            value={this.state.boxWidth}
            disabled={this.props.resizingDisabled}
            onChange={this.onChangeWidth}
          />

          <FloatInput
            label="Height"
            id="bounding-box-height-input"
            value={this.state.boxHeight}
            disabled={this.props.resizingDisabled}
            onChange={this.onChangeHeight}
          />

          <Select value={this.state.units} onChange={this.onChangeUnits} classes={{ select: 'sizeUnitSelect' }}>
            <MenuItem id="selectPixelUnit" value="px">
              px
            </MenuItem>
            <MenuItem id="selectInchUnit" value="in">
              in
            </MenuItem>
          </Select>
        </AnnotationsOptionsRow>

        <AnnotationsOptionsRow>
          <FormControlLabel
            control={
              <Checkbox
                color="default"
                id="scaleTogetherCheckbox"
                onChange={this.onChangeScaleTogether}
                disabled={this.props.individualScalingDisabled}
                checked={this.state.scaleTogether}
              />
            }
            label="Scale height and width together"
          />
        </AnnotationsOptionsRow>
      </AnnotationOptionsSection>
    );
  }
}
